<script setup lang="ts">
import { onMounted, ref, shallowRef } from "vue";
import { GetCtyunDeviceVideoUrl } from "./ctyun-player";
import EasyPlayer from "./easyPlayer.vue";
import { TzjPlayerProps } from "./tzj-player";

// 天翼云播放器
defineOptions({
  name: "CtyunPlayer",
});

const props = defineProps(TzjPlayerProps);
const videoUrl = ref("");
const showBottomBar = ref(false);
const errorMsg = ref("");

// 播放器相关状态
const playerRef = shallowRef();
const isPlaying = ref(false); // 是否播放，播放true,暂停false
const isFullscreen = ref(false);
const isVolumeMuted = ref(true);

const audioMsg = ref({
  type: "",
  msg: "",
}); // 语音对讲提示信息

const initPlayer = async () => {
  try {
    if (!props.deviceSerial) {
      errorMsg.value = "设备序列号不能为空";
      return;
    }

    const url = await GetCtyunDeviceVideoUrl(props.deviceSerial);
    if (!url) {
      errorMsg.value = "获取视频播放地址失败";
      return;
    }

    videoUrl.value = url;
    errorMsg.value = "";
  } catch (error) {
    if (props.debug) {
      console.error("初始化天翼云播放器失败:", error);
    }
    errorMsg.value = `初始化失败: ${error instanceof Error ? error.message : "未知错误"}`;
  }
};

/**
 * 播放视频
 */
const playerPlay = (): void => {
  playerRef.value?.play();
};

/**
 * 暂停视频
 */
const playerPause = (): void => {
  playerRef.value?.pause();
};

/**
 * 截图
 */
const playerScreenShot = (): void => {
  playerRef.value?.screenshot();
};

/**
 * 切换全屏状态
 * @param fullscreen 是否全屏
 */
const playerFullscreen = (fullscreen: boolean): void => {
  isFullscreen.value = fullscreen;
  playerRef.value?.fullscreen(fullscreen);
};

// 获取视频信息,判断当前是否为静音状态
const handleVideoInfo = () => {
  // 判断是否静音
  isVolumeMuted.value = playerRef.value?.isMute();
};
const handleMute = (mute: boolean) => {
  isVolumeMuted.value = mute;
};
const handleSetMute = (volume: number) => {
  playerRef.value?.setMute(volume);
};

onMounted(() => {
  initPlayer();
});
</script>

<template>
  <EasyPlayer
    ref="playerRef"
    :url="videoUrl"
    :debug="debug"
    :access-token="accessToken"
    :platform-type-name="platformTypeName"
    :device-serial="deviceSerial"
    :area-name="areaName"
    :pause-mode="pauseMode"
    @mouseenter="showBottomBar = true"
    @mouseleave="showBottomBar = false"
    @pause="isPlaying = false"
    @play="isPlaying = true"
    @video-info="handleVideoInfo"
    @mute="handleMute"
  >
    <template #customBar>
      <div v-if="showBottomBar" class="i-easy-player-bar">
        <!-- 左侧：播放/暂停 -->
        <div class="i-easy-player-bar-item i-easy-player-bar-left">
          <!-- 播放 -->
          <div
            v-if="!isPlaying"
            class="i-easy-player-bottom-bar"
            @click="playerPlay"
          >
            <i class="iconfont icon-playfill" />
          </div>
          <!-- 暂停 -->
          <div v-else class="i-easy-player-bottom-bar" @click="playerPause">
            <i class="iconfont icon-stop" />
          </div>

          <div v-if="!isVolumeMuted" class="i-easy-player-bottom-bar">
            <!-- 音量图标 -->
            <i
              class="iconfont icon-notificationfill"
              @click="handleSetMute(1)"
            />
          </div>
          <div v-else class="i-easy-player-bottom-bar">
            <!-- 静音图标 -->
            <i
              class="iconfont icon-notificationforbidfill"
              @click="handleSetMute(0)"
            />
          </div>
        </div>
        <!-- 截图,全屏 -->
        <div class="i-easy-player-bar-item i-easy-player-bar-right">
          <!-- 截图 -->
          <div class="i-easy-player-bottom-bar" @click="playerScreenShot">
            <i class="iconfont icon-picfill" />
          </div>
          <!-- 全屏 -->
          <div
            v-if="!isFullscreen"
            title="全屏"
            class="i-easy-player-bottom-bar"
            @click="playerFullscreen(true)"
          >
            <i class="iconfont icon-full" />
          </div>
          <!-- 取消全屏 -->
          <div
            v-if="isFullscreen"
            class="i-easy-player-bottom-bar"
            @click="playerFullscreen(false)"
          >
            <i class="iconfont icon-reduction" />
          </div>
        </div>
      </div>
    </template>
  </EasyPlayer>
  <!-- 语音对讲错误提示 -->
  <div
    v-if="audioMsg.msg"
    class="i-tzj-player__voice-default"
    :class="`i-tzj-player__voice-${audioMsg.type}`"
  >
    {{ audioMsg.msg }}
  </div>
</template>
